import { ApplicationRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { BodyScrollEvent, CellClickedEvent, ColDef, DisplayedColumnsChangedEvent, GridApi, GridOptions, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IRowNode, RowClickedEvent, RowDoubleClickedEvent, RowDragEvent, RowNode, RowSelectedEvent, SelectionChangedEvent } from 'ag-grid-community';
import * as lo_ from 'lodash';
import { Subscription } from 'rxjs';
import * as _ from 'underscore';
import { isArray, isNullOrUndefined, isNumber, isString } from '../../models/utils';
import { ContextMenuOption } from '../../models/context-menu-option';
import { DrRequestInfo } from '../../models/dr-request-info';
import { HelpIndex, MessageType, UpdateStatusType, Endpoint, PasswordSourceType, FilterAttribute } from '../../models/enums';
import { CellActionType, FilterTemplateType, GenericColDef, GridContext, GridRowSelectionType } from '../../models/grid-context';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { XMLService } from '../../services/xml/xml.service';
import { GridCellRenderer } from '../grid-templates/grid-cell/grid-cell-renderer';
import { GridWorkbasketActionsCellRenderer } from '../grid-templates/grid-cell/grid-workbasket-actions-cell-renderer';
import { GridHeaderComponent } from '../grid-templates/grid-header/grid-header.component';
import { PagerComponent } from '../pager/pager.component';
import { CommonWithRestService } from '../../services/common/common-with-rest.service';
import { ContextMenuComponent } from '../context-menu/context-menu.component';
import { Corpus, RepresentationSet, SharedState } from '../../models/corpus';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { SearchResult } from '../../models/search-result';
import { ViewType } from '../../models/view';
import { Job } from '../../models/job';
import { ComboBoxCellComponent } from '../grid-templates/combo-box-cell/combo-box-cell.component';

@Component({
    standalone: false,
    selector: 'res-generic-grid',
    templateUrl: './generic-grid.component.html',
    styleUrls: ['./generic-grid.component.scss']
})
export class GenericGridComponent implements OnInit, OnDestroy {
    // needed so the component element maximized its content
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('contextMenu') contextMenu: ContextMenuComponent;
    @ViewChild('headerContextMenu', { static: true }) headerContextMenu: ContextMenuComponent;
    @ViewChild('pager') pager: PagerComponent;
    // used to build the busy spinner text
    @Input() gridName = '';
    // used as a prefix for component content, component header, component grid and its cells and headers
    @Input() automationId: string;
    // if set, the help button is visible, but only if the grid has a header template
    @Input() helpIndex: HelpIndex;
    // used to know when the grid should refresh, an enum value of this type is usually fired from the http poller
    @Input() updateStatusType: string;

    // used to insert elements above the grid, if this is missing the grid won't display a help button, so the navigation parent (main content/settings) does
    @Input() headerTemplate: TemplateRef<any>;
    // used to insert elements inside the pager
    @Input() pagerTemplate: TemplateRef<any>;
    // used by pager to show button titles
    @Input() pagerName: string;
    // used to cover the whole grid when needed
    @Input() overlayTemplate: TemplateRef<any>;
    // used to hide the pager
    @Input() pagerVisible = true;
    // used to disable the pager. use this when paging is not supported by the api but still want a way to refresh and see the total number of items
    @Input() pagerDisabled = false;
    // show or hide refresh icon in pager component
    @Input() isRefreshIconHidden = false;
    // don't trigger redraw operation on context menu open/close
    @Input() isGridWithDurationColumn = false;
    // use a cancelable call for the initial data pull
    @Input() preferObservable = false;
    // show context menu permissions
    @Input() hasContextMenuPermissions = true;

    @Input() isBusy = false;
    @Input() isBusyMessage: string;
    @Input() isBusyOnInit = false;

    // used when data is already loaded - populate grid
    @Input() initialData: Array<any>;

    @Input() rowDragEntireRow = false;
    @Input() rowDragManaged = false;

    // used for report pinned row
    @Input() pinnedRow;
    // used for storage grid top row pinned
    @Input() pinnedTopRowData;
    // custom grid classes (no selection, no hover, no odd/even styles)
    @Input() customGridClass = '';
    // grid row height callback
    @Input() rowHeightCallback: (params: any) => number;
    // custom grid row height
    @Input() rowHeight = 30;
    // custom grid header height
    @Input() headerHeight = 29;
    // the column array for the grid
    @Input() columnDefs: Array<GenericColDef>;
    // type of grid selection: none, single, single with checkbox, multiple or multiple with checkbox
    @Input() rowSelection = GridRowSelectionType.none;
    // flag used to tell grid it has grouping
    @Input() rowsHaveGrouping: boolean;
    // flag used to tell grid it has tree-like grouping
    @Input() rowsHaveTreeGrouping: boolean;
    // flag used to tell grid it has header right click context menu
    @Input() showHeaderContextMenu = true;
    // flag used to tell grid it has custom cell template framework
    @Input() hasCustomCellTemplates = false;
    // the api request info, containing endpoint, response array field name and request object
    @Input() requestInfo: DrRequestInfo;
    // suppressRowTransform value for grid
    @Input() suppressRowTransform: boolean;
    // max height to increase viewport
    @Input() maxHeightToIncreaseViewport = 0;
    // used to set the grid default sort by and sort direction and optionally a map for the context menu item (summaries specify it to reuse the nav tree context menus)
    private _gridContext: GridContext = new GridContext({
        onHeaderClick: (sortBy: string, sortAscending: boolean) => this.onHeaderClick(sortBy, sortAscending),
        onHeaderContextMenu: (item: GenericColDef, event: MouseEvent) => this.onHeaderContextMenu(item, event),
        onCellContextMenu: (params: ICellRendererParams, event: MouseEvent) => this.onCellContextMenu(params, event),
        onTaskActionClick: (action: CellActionType, item: any) => this.onJobManagementTaskActionClick(action, item),
        onFilterApply: (params: GenericColDef, filterType: FilterTemplateType, filterColumn: string, isResetAllFilter: boolean) => this.onFilterApply(params, filterType, filterColumn, isResetAllFilter),
        clearAllFilters: new EventEmitter<any>(),
        clearAllFilterInputs: new EventEmitter<any>()
    });
    get gridContext(): GridContext {
        return this.gridOptions ? <GridContext>this.gridOptions.context : this._gridContext;
    }
    @Input()
    set gridContext(context: GridContext) {
        if (context) {
            Object.assign(this._gridContext, context);
        }
    }
    // array of options for context menu that shows on item right click or context menu cell click
    @Input() contextMenuOptions: Array<ContextMenuOption>;

    // used to give custom class names for rows (disabled rows, grouped rows)
    @Input() rowClassRules: { [cssClassName: string]: (() => void | string); };
    // callback used to process the response (reading a custom totalItems for the pager, parenting items)
    @Input() responseCallback: (response: any) => void;
    // callback used to paginate grid based on other call
    @Input() gridPagerCustomCallback: (response: any) => void;
    // callback used to paginate grid based on other call
    @Input() gridPagerAfterLoadCustomCallback: () => void;
    // callback used for custom behaviour after grid has loaded data
    @Input() loadDataFinishedCallback: () => void;
    // callback used for custom behaviour after grid headerfilter
    @Input() onCustomFilterCallback: () => void;
    // callback used to map the response array to specific models
    @Input() responseArrayCallback: (value: any, fullResponse: any) => void;
    // callback used to filter the response array
    @Input() responseArrayFilter: (value: any) => void;
    // callback used for custom cell click events (family expansion, open doc viewer)
    @Input() cellClickedCallback: (rowNode: RowNode, action: CellActionType, event?: CellClickedEvent) => void;
    // callback used for custom cell double click events
    @Input() cellDoubleClickedCallback: (rowNode: RowNode) => void;
    // callback used for custom grid selection
    @Input() onSelectionChanged: (event: SelectionChangedEvent) => void;
    // callback used for custom non specific grid cell click: use this only when cellClickedCallback and its action enum is not enough
    @Input() onCellClicked: (event: CellClickedEvent) => void;
    // // callback used to signal that columns visibility changed
    @Input() onColumnVisibilityChanged: () => void;
    // callback used for custom error handling
    @Input() customErrorHandlingCallback: () => void;
    // Event triggered when there is a change in the state of columns initially loaded, column move, columns hidden/shown etc., 
    @Input() onDisplayedColumnsChanged: (event: DisplayedColumnsChangedEvent) => void;
    // callback used to select only enabled rows
    @Input() isRowSelectable: (rowNode: RowNode) => boolean;
    // callback used to listen when a row is clicked
    @Input() onRowClickedCallback: (event: RowClickedEvent) => boolean;
    // callback used to listen when a row was drag (grid items were reordered)
    @Input() onRowDragEnd: (event: RowDragEvent) => void;
    // callback used to listen when the mouse has left the grid while dragging
    @Input() onRowDragLeave: (event: RowDragEvent) => void;
    // callback used to listen when a row is clicked
    @Input() onRowSelectedCallback: (event: RowSelectedEvent) => boolean;


    headerContextMenuOptions = new Array<ContextMenuOption>();

    gridOptions: GridOptions;
    gridApi: GridApi;
    private data: Array<any>;
    totalItems = 0;
    selectedRows: Array<any> = new Array<any>();
    disabledRows: Array<any> = new Array<any>();
    allRowsSelected: boolean;
    filters: Array<any> = new Array<any>();
    gridCheckedItems: Array<any> = new Array<any>();
    setCheckedItems: boolean;
    isDataFiltered: boolean;
    initialLoadData: any;
    suppressRefresh = false;
    loadAndFilter: boolean;
    lastFilter: any;
    flattenedChildRows: Array<any> = [];

    private entityChangedEvent: Subscription;
    private contextMenuNodes: Array<RowNode>;
    private lastGridSizeChangedEvent: GridSizeChangedEvent;
    private subscription: Subscription;

    constructor(private restService: RestService, private commonService: CommonService, private applicationRef: ApplicationRef,
        private xmlService: XMLService, private commonWithRestService: CommonWithRestService, private elemRef: ElementRef) { }

    ngOnInit(): void {
        if (!isNumber(this.headerHeight)) {
            this.headerHeight = 29;
        }
        if (!isNumber(this.rowHeight)) {
            this.rowHeight = 30;
        }
        if (this.columnDefs?.length > 0) {
            this.columnDefs.forEach(colDef => {
                colDef.cellRenderer = colDef.cellRenderer ? colDef.cellRenderer : 'gridCellRenderer';
            })
        }
        this.gridOptions = <GridOptions>{
            suppressPropertyNamesCheck: true,
            suppressLoadingOverlay: true,
            suppressNoRowsOverlay: true,
            rowBuffer: 1000,
            suppressMaxRenderedRowRestriction: true,
            suppressDragLeaveHidesColumns: true,
            suppressColumnVirtualisation: true,
            suppressScrollOnNewData: true,
            suppressRowTransform: this.suppressRowTransform ?? false,
            isRowSelectable: this.isRowSelectable ? this.isRowSelectable : (node: RowNode) => {
                return (node.data && !node.data.isDisabled);
            },
            rowDragMultiRow: this.rowDragManaged,
            rowSelection: (this.rowSelection === GridRowSelectionType.multiple ||
                this.rowSelection === GridRowSelectionType.multipleCheckbox ||
                this.rowSelection === GridRowSelectionType.multipleCheckboxWithRowClickSelection) ? 'multiple' : 'single',
            suppressCellFocus: true,
            suppressRowClickSelection: !this.rowSelection ||
                this.rowSelection === GridRowSelectionType.none ||
                this.rowSelection === GridRowSelectionType.singleCheckbox ||
                this.rowSelection === GridRowSelectionType.multipleCheckbox,
            rowMultiSelectWithClick: this.rowSelection === GridRowSelectionType.singleCheckboxWithRowClickSelection ||
                this.rowSelection === GridRowSelectionType.multipleCheckboxWithRowClickSelection,
            headerHeight: this.gridContext.inlineGridFilter ? this.headerHeight + 34 : this.headerHeight,
            getRowHeight: this.rowHeightCallback,
            rowHeight: this.rowHeight,
            defaultColDef: {
                headerComponent: <new () => GridHeaderComponent>GridHeaderComponent,
                cellRenderer: this.hasCustomCellTemplates ? undefined : 'gridCellRenderer',
                resizable: true,
                minWidth: 30
            },
            components: {
                'gridCellRenderer': <any>GridCellRenderer,
                'gridWorkbasketActionsCellRenderer': <any>GridWorkbasketActionsCellRenderer
            },
            columnDefs: this.columnDefs,
            icons: { rowDrag: '<img src="assets/images/drag_grey.png" style="margin-left: 7px;">' },
            rowClassRules: {
                'child-row': function (params) {
                    return (params.data && params.data.isChild);
                },
                'context-menu-row': (params) => {
                    return (params.node && this.contextMenuNodes && this.contextMenuNodes.includes(params.node as RowNode));
                },
                'disabled-row': (params) => {
                    return (params.data && params.data.isDisabled);
                }
            },
            onBodyScroll: (event: BodyScrollEvent) => {
                if (event?.direction === 'horizontal') {
                    let element = this.elemRef.nativeElement.querySelectorAll('.ag-header-container');
                    if (event.left > 0) {
                        element[0]?.setAttribute('style', 'transform:translateX(-' + event.left + 'px)');
                    } else {
                        element[0]?.setAttribute('style', 'transform:translateX(0px)');
                    }
                }
            },
            onDisplayedColumnsChanged: (event: DisplayedColumnsChangedEvent) => {
                if (this.onDisplayedColumnsChanged) {
                    this.onDisplayedColumnsChanged(event);
                }
            },
            onRowClicked: (event: RowClickedEvent) => {
                if (this.onRowClickedCallback) {
                    this.onRowClickedCallback(event);
                }
            },
            onGridReady: (event: GridReadyEvent) => {
                if (!this.gridApi) {
                    this.gridApi = event.api;
                }

                const hasColumnFilter = _.filter(this.gridApi.getColumns(), (column) => column.colDef.filterType);
                if (hasColumnFilter.length > 0) {
                    this.gridContext.inlineGridFilter = true;
                    this.gridApi.setGridOption('headerHeight', this.gridContext.inlineGridFilter ? this.headerHeight + 34 : this.headerHeight);
                    const hasResetAllFiltersContextMenu = _.where(this.headerContextMenuOptions, { name: 'Reset All Filters' });
                    if (this.showHeaderContextMenu && hasResetAllFiltersContextMenu?.length === 0) {
                        this.headerContextMenuOptions.push(new ContextMenuOption({
                            name: 'Reset All Filters',
                            iconClass: 'fa-close',
                            iconColor: 'color-grey-08',
                            resetFilterClass: true,
                            enabled: this.gridContext.inlineGridFilter,
                            passive: false,
                            click: () => {
                                this.gridContext.clearAllFilters.emit();
                            }
                        }),
                            new ContextMenuOption({ divider: true }));
                    }
                }

                if (this.gridApi?.getColumns()?.length > 0) {
                    this.gridApi.getColumns().forEach(column => {
                        const colDef = <GenericColDef>column.getColDef();
                        if (colDef && colDef.headerName) {
                            const isContextMenuAvailable = _.where(this.headerContextMenuOptions, { name: colDef.headerName });
                            if (this.showHeaderContextMenu && isContextMenuAvailable?.length === 0) {
                                this.headerContextMenuOptions.push(new ContextMenuOption({
                                    name: colDef.headerName,
                                    enabled: !colDef.lockVisible,
                                    passive: true,
                                    checked: column.isVisible(),
                                    click: () => {
                                        const headers = _.filter(this.gridApi.getColumns(), (column) => column.colDef.filterType === FilterTemplateType.date);
                                        if (headers.length > 0 && this.gridName === 'Job History') {
                                            this.commonService.sendMessage(MessageType.gridHeadersChanged, colDef);
                                        }
                                        this.gridApi.setColumnsVisible([column.getColId()], !column.isVisible());
                                        this.gridApi.sizeColumnsToFit();
                                        if (this.onColumnVisibilityChanged) {
                                            this.onColumnVisibilityChanged();
                                        }
                                    }
                                }));
                            }
                        }
                    });
                }

                this.refreshColumns();
                if (!this.isBusy) {
                    if (this.isBusyOnInit) {
                        this.isBusyMessage = `Loading ${this.gridName} ...`;
                        this.isBusy = true;
                    }
                    if (this.initialData) {
                        this.setData(this.initialData);
                    } else if (this.preferObservable) {
                        this.loadObservableData();
                    } else {
                        const preventLoadDataWhenCalledFromReportDetails = event && event.type === 'reportDetailsGridReady' ? true : false;
                        if (!preventLoadDataWhenCalledFromReportDetails) {
                            this.loadData();
                        }
                    }
                }
            },
            onGridSizeChanged: (event: GridSizeChangedEvent) => {
                if (event) {
                    if (!this.lastGridSizeChangedEvent || (this.lastGridSizeChangedEvent.clientWidth !== event.clientWidth)) {
                        this.refreshColumns();
                    }
                    this.lastGridSizeChangedEvent = event;
                }
            },
            onSelectionChanged: (event: SelectionChangedEvent) => {
                this.handleRowSelection();
                if (this.onSelectionChanged) {
                    this.onSelectionChanged(event);
                }
            },
            onCellClicked: (event: CellClickedEvent) => {
                const mouseEv = (<MouseEvent>event.event);
                const noTextSelectClass = ' no-select';
                const eventNode: any = event?.event?.target;
                const selectedNode: any = event.node;

                if ((eventNode.nodeName === 'SELECT' && event?.event?.defaultPrevented) ||
                    (event?.colDef?.cellRenderer?.name === ComboBoxCellComponent.name && this.gridOptions.rowMultiSelectWithClick)) {
                    event.event.preventDefault();
                    event.event.stopPropagation();
                    event.node.setSelected(!selectedNode.selected);
                }
                if (mouseEv.shiftKey) {
                    this.customGridClass += noTextSelectClass;
                } else if (this.customGridClass.includes(noTextSelectClass)) {
                    this.customGridClass = this.customGridClass.replace(noTextSelectClass, '');
                    const sel = mouseEv.view.getSelection();
                    sel.removeAllRanges();
                }
                if (event && event.node && (<MouseEvent>event.event) && event.event.target && (<HTMLElement>event.event.target)) {
                    const actionType = <CellActionType>(<HTMLElement>event.event.target).getAttribute('data-cell-action-type');
                    if (actionType) {
                        this.onCellClick(event, actionType);
                    }
                }
                if (this.onCellClicked) {
                    this.onCellClicked(event);
                }
            },
            onRowDoubleClicked: (event: RowDoubleClickedEvent) => {
                this.onRowDoubleClicked(event);
            },
            onRowSelected: (event?: any) => {
                if (event && event.node && !event.node.selected) {
                    const columnId = this.getColumnFieldId();
                    if (columnId) {
                        this.removeCheckedOrFilteredItems('gridCheckedItems', event.data[columnId], columnId);
                    }
                }
                if (this.onRowSelectedCallback) {
                    this.onRowSelectedCallback(event);
                }
            },
            onRowDragEnd: (event: RowDragEvent) => {
                if (this.onRowDragEnd) {
                    this.onRowDragEnd(event);
                }
            },
            onRowDragLeave: (event: RowDragEvent) => {
                if (this.onRowDragLeave) {
                    this.onRowDragLeave(event);
                }
            },
            context: this._gridContext
        };
        if (this.updateStatusType) {
            this.entityChangedEvent = this.commonService.getMessage(this.updateStatusType !== MessageType.dataArea ? MessageType.entityChanged : MessageType.dataArea).subscribe((data) => {
                if ((data?.isDeleteOperation ? data.updateStatusType : data) === this.updateStatusType && !this.suppressRefresh) {
                    if (this.updateStatusType === UpdateStatusType.RESULT_VIEW) {

                        // TODO - remove isFindDuplicates && selectedErrorNavigationNode after tree and selected item refresh will be refactored for search history items
                        //  currently blocks the refresh of the previous selected item triggered multiple times by MessageType.entityChanged and loading it only once
                        if ((!this.commonService.selectedErrorNavigationNode && !this.commonService.isFindDuplicates) ||
                            (this.commonService.selectedErrorNavigationNode && this.commonService.selectedErrorNavigationNode.name !== this.commonService.selectedNavigationNode.name)) {
                            this.loadData(0 || this.requestInfo && this.requestInfo.data && isNumber(this.requestInfo.data.startIndex) ? this.requestInfo.data.startIndex : 0,
                                PagerComponent.itemsOnPage, true);
                        }
                    } else {
                        if (this.updateStatusType === UpdateStatusType.WORK_FLOW) {
                            setTimeout(() => this.loadGridData(), 1000);  //added 1s delay in order to prevent backed error when 2 getWorkflows are triggered in the same time(one for grid and one for tree), error caused by workflow versioning
                        } else {
                            this.loadGridData();
                        }
                    }
                }
            });
        }

        if (this.gridContext) {
            Object.assign(this.gridOptions.context, this.gridContext);
        }
        if (this.rowClassRules) {
            Object.assign(this.gridOptions.rowClassRules, this.rowClassRules);
        }
        if (this.rowSelection === GridRowSelectionType.single ||
            this.rowSelection === GridRowSelectionType.multiple ||
            this.rowSelection === GridRowSelectionType.multipleCheckboxWithRowClickSelection ||
            this.rowSelection === GridRowSelectionType.singleCheckboxWithRowClickSelection) {
            this.customGridClass += ' ag-row-click-selection';
        }
    }

    loadGridData(): void {
        this.loadData(0 || this.requestInfo && this.requestInfo.data && isNumber(this.requestInfo.data.startIndex) ? this.requestInfo.data.startIndex : 0, PagerComponent.itemsOnPage, true);
    }

    loadObservableData(startIndex: number = (0 || this.requestInfo && this.requestInfo.data && isNumber(this.requestInfo.data.startIndex) ? this.requestInfo.data.startIndex : 0),
        count: number = PagerComponent.itemsOnPage, refresh = false): void {

        if (this.subscription && !this.subscription.closed) {
            this.subscription.unsubscribe();
        }
        this.subscription = this.restService.postWithObservable(this.requestInfo.endpoint, this.requestInfo.data).subscribe((resp) => {
            this.data = new Array<any>();
            this.totalItems = 0;
            if (resp) {
                if (this.responseCallback) {
                    this.responseCallback(resp);
                }
                if (isArray(resp[this.requestInfo.responseName])) {
                    if (this.responseArrayCallback) {
                        this.data = (resp[this.requestInfo.responseName] as Array<any>).map((item) =>
                            this.responseArrayCallback(item, resp));
                    } else {
                        this.data = (resp[this.requestInfo.responseName] as Array<any>);
                        this.totalItems = resp.totalCount || resp.numberResults || resp.totalTasks || resp.totalProjects || this.data.length;
                        this.loadDataFinished(refresh);
                    }
                }
            }
        }, (error) => {
            if (error) {
                this.commonService.openErrorModal(error);
                if (this.customErrorHandlingCallback) {
                    this.customErrorHandlingCallback();
                }
            }
        });
    }

    loadData(startIndex: number = (0 || this.requestInfo && this.requestInfo.data && isNumber(this.requestInfo.data.startIndex) ? this.requestInfo.data.startIndex : 0),
        count: number = PagerComponent.itemsOnPage, refresh = false, responseData?: any): void {
        if (responseData || (this.requestInfo && this.requestInfo.endpoint && this.requestInfo.data && this.requestInfo.responseName)) {
            let reselectItems = false;
            if (this.pagerVisible) {
                if (!this.pagerDisabled && this.gridName !== 'Email Threads' && this.gridName !== 'Short Message Threads' && this.gridName !== 'Report Details' && this.gridName !== 'Create Manifest Metadata') {
                    reselectItems = this.requestInfo.data.startIndex === startIndex;
                    this.requestInfo.data.startIndex = startIndex;
                    this.requestInfo.data.count = count;
                }
                if (this.requestInfo.data.dateReportRequests && this.requestInfo.data.dateReportRequests.length > 0) {
                    this.gridContext.setRequestFields(this.requestInfo.data.dateReportRequests[0]);
                } else if (this.requestInfo.data.reportRequests && this.requestInfo.data.reportRequests.length > 0) {
                    this.gridContext.setRequestFields(this.requestInfo.data.reportRequests[0]);
                } else {
                    this.gridContext.setRequestFields(this.requestInfo.data);
                }
            }

            this.isBusyMessage = `Loading ${this.gridName} ...`;
            if (!refresh || this.gridName === 'Tasks') {
                this.isBusy = this.commonService.suppressGridSpinnerIfPoller || this.commonService.suppressGridSpinner ? false : true;
            }

            this.loadAndFilter = this.gridContext.loadAndFilter;

            const optionalCall = !responseData ? (() => Promise.resolve(
                this.restService.post(this.requestInfo.endpoint, this.requestInfo.data)
            )) : () => Promise.resolve(responseData);

            optionalCall().then(response => {
                this.data = new Array<any>();
                this.totalItems = 0;
                if (response) {
                    if (this.responseCallback) {
                        this.responseCallback(response);
                    }
                    if (isArray(response[this.requestInfo.responseName])) {
                        if (this.responseArrayCallback) {
                            this.data = (response[this.requestInfo.responseName] as Array<any>).map((item) =>
                                this.responseArrayCallback(item, response));
                            Promise.all(this.data).then((results) => {
                                this.data = results;
                                this.totalItems = response.totalCount || response.numFilteredWords || response.numberResults || response.totalTasks || response.totalEvents || response.totalProjects || this.data.length;
                                this.loadDataFinished(refresh, reselectItems);
                            });
                        } else {
                            this.data = (response[this.requestInfo.responseName] as Array<any>);
                            this.totalItems = response.totalCount || response.numberResults || response.totalTasks || response.totalEvents || response.totalProjects || this.data.length;
                            this.loadDataFinished(refresh, reselectItems);
                        }
                    }
                }
                this.commonService.userSelectionClick = false;
            }, error => {
                this.isBusy = false;
                this.commonService.userSelectionClick = false;
            });
        }
    }

    loadDataFinished(refresh = false, reselectItems = false): void {
        if (this.updateStatusType === UpdateStatusType.SAVED_SEARCH) {
            this.data.forEach(element => {
                const searchTypeAndTask = (Object)(this.xmlService.xmlToQueryConverter(element.searchQuery, true));
                element.searchTask = searchTypeAndTask.query;
                const targetName = element.attributes.find(f => f.name === 'targetViewname');
                if (targetName) {
                    element.searchTask += ' in ' + targetName.value;
                }
            });
        }
        if (this.updateStatusType === UpdateStatusType.CLUSTER_VIEW) {
            reselectItems = true;
            this.selectedRows = this.gridApi?.getSelectedRows().filter(row => !row.isDisabled);
        }

        if (this.responseArrayFilter) {
            this.data = this.data.filter(this.responseArrayFilter);
        }

        if (this.data && this.data.length > 0 && this.pagerVisible === false && this.gridContext.sortBy) {
            const sortBy = this.gridContext.sortBy;
            this.data = _.sortBy(this.data, (item) => {
                if (!item || isNullOrUndefined(item[sortBy])) {
                    return '';
                }
                if (isString(item[sortBy])) {
                    return item[sortBy].toLowerCase();
                }
            });

            if (this.gridName === 'Report Grid' || (this.gridName === 'Patterns' && (sortBy === 'enabled' || sortBy === 'storeValue'))) {
                this.data = this.data.sort((a, b) => a[sortBy] - b[sortBy]);
            }

            if (this.gridContext.sortAscending === false) {
                this.data.reverse();
            }

            // add empty DragDropData row to the end of the list if it is added as the first row - affected by sorting
            if (this.data?.length > 0 && this.data[0]?.isEmptyDropRow) {
                this.data.push(this.data.shift());
            }
        }

        this.gridContext.filterData = this.data;
        if (!this.isDataFiltered) {
            this.initialLoadData = this.data;
        }
        const originalSelectedRows = this.gridApi?.getSelectedRows();
        if (this.gridApi && !this.gridApi.isDestroyed()) {
            if (this.pinnedRow) {
                this.gridApi.setGridOption('pinnedBottomRowData', [this.pinnedRow]);
            }
            this.gridApi.setGridOption('rowData', this.data);
        }
        if (this.rowsHaveGrouping) {
            this.getRows().forEach(row => {
                if (row.data.isExpanded) {
                    this.expandCollapseRow(row, false, false);
                }
            });
        }

        if (this.gridName === 'Services Realm Nodes') {
            reselectItems = true;
        }

        // reselect items
        if (this.gridApi) {
            if (reselectItems && originalSelectedRows?.length > 0) {
                this.gridApi.forEachNode((node) => {
                    if ((node.data?.handle && originalSelectedRows.find(r => r.handle === node.data.handle)) ||
                        (node.data?.serviceName && originalSelectedRows.find(r => r.serviceName === node.data.serviceName))) {
                        node.setSelected(true);
                    }
                });
            }
        }
        if (this.loadDataFinishedCallback) {
            this.loadDataFinishedCallback();
        }
        if (!refresh) {
            this.refreshColumns();
        }
        if (this.data) {
            this.disabledRows = this.data.filter(item => item?.isDisabled);
        }

        if (this.setCheckedItems) {
            this.checkSelectedItems();
            this.setCheckedItems = false;
        }

        if (this.loadAndFilter) {
            this.initialLoadData = this.data;
            if (this.filters.length > 0 && this.gridApi) {
                const filterAttribute = this.filters[0].value;
                const filter = _.filter(this.gridApi.getColumns(), (column) => column.colDef.filterType);
                let filterType = filter?.length > 0 ? filter[0].colDef.filterType : '';
                if (this.filters[0].hasMultipleFilteredValues) {
                    filterType = this.filters[0].filterType;
                }
                if (filterType && this.gridName !== 'Job History') {
                    this.onFilterApply(filterAttribute, filterType, this.filters[0].filterColumn);
                }
            }
        }

        if (this.maxHeightToIncreaseViewport > 0) {
            const bodyViewportElement = this.elemRef.nativeElement.querySelectorAll('.ag-body-viewport');
            if (bodyViewportElement?.length > 0 && bodyViewportElement[0] && this.data?.length > 0) {
                const calculatedHeight = this.data.length * this.rowHeight + this.maxHeightToIncreaseViewport;
                if (calculatedHeight > bodyViewportElement[0].scrollHeight) {
                    const heigthToAdd = (calculatedHeight - bodyViewportElement[0].scrollHeight) < 140 ? calculatedHeight - bodyViewportElement[0].scrollHeight : 140;
                    const centerColsClipperElement = this.elemRef.nativeElement.querySelectorAll('.ag-center-cols-clipper');
                    if (centerColsClipperElement?.length > 0 && centerColsClipperElement[0]) {
                        const newHeigth = centerColsClipperElement[0].scrollHeight + heigthToAdd;
                        centerColsClipperElement[0].setAttribute('style', 'height:' + newHeigth + 'px');
                        this.gridApi.sizeColumnsToFit();
                    }
                }
            }
        }

        // check if all items are selected, to reselect after reload
        this.handleRowSelection(true);

        this.commonService.suppressGridSpinnerIfPoller = false;
        // TODO - remove isFindDuplicates after tree and selected item refresh will be refactored for search history items
        // currently used to keep the busy spinner on if isFindDuplicates call is made until everything finishes
        this.isBusy = (this.commonService.isFindDuplicates && this.requestInfo.endpoint === Endpoint.viewManager_listDocuments) || (this.gridName === 'Services' && this.commonService.keepGridSpinner) ? true : false;
    }

    handleRowSelection(selectAll = false) {
        if (this.rowSelection === GridRowSelectionType.singleCheckbox ||
            this.rowSelection === GridRowSelectionType.singleCheckboxWithRowClickSelection ||
            this.rowSelection === GridRowSelectionType.multipleCheckbox ||
            this.rowSelection === GridRowSelectionType.multipleCheckboxWithRowClickSelection) {

            if (this.gridApi) {
                if (selectAll && this.allRowsSelected) {
                    this.gridApi.selectAll();
                }
                const allSelectedRows = this.gridApi.getSelectedRows();
                this.selectedRows = allSelectedRows?.length > 0 ? allSelectedRows.filter(row => !row.isDisabled) : [];
                this.allRowsSelected = (this.selectedRows.length > 0 && this.selectedRows.length === this.gridApi.getDisplayedRowCount() - this.disabledRows.length);

                const columnId = this.getColumnFieldId();
                this.selectedRows.forEach((item) => {
                    const itemFound = item && item.handle ? _.indexOf(_.pluck(this.gridCheckedItems, 'handle'), item.handle) : _.indexOf(_.pluck(this.gridCheckedItems, columnId), item[columnId]);
                    if (itemFound === -1) {
                        this.gridCheckedItems.push(item);
                    }
                });
            }
        }
    }

    getData(): Array<any> {
        const itemList = [];
        if (this.gridApi) {
            this.gridApi.forEachNode(node => {
                itemList.push(node.data);
                if (node.data.children && !node.data.isExpanded) {
                    node.data.children.forEach(child => {
                        itemList.push(child);
                    });
                }
            });
        }
        return itemList;
    }

    setData(theData: Array<any>, refresh = false, reselectItems = false, retrySetData = true): void {
        if (this.gridApi) {
            this.data = theData;
            this.loadDataFinished(refresh, reselectItems);
        } else {
            setTimeout(() => {
                if (retrySetData) {
                    this.setData(theData, refresh, reselectItems, false);
                }
            });
        }
    }

    findNode(predicate: (nodeData: RowNode) => boolean, select?: boolean): RowNode {
        let foundNode: RowNode = null;
        if (this.gridApi) {
            this.gridApi.forEachNode((node: RowNode) => {
                if (predicate(node)) {
                    foundNode = node;
                    if (select) {
                        this.selectNode(node);
                    }
                    return;
                }
            });
        }
        return foundNode;
    }

    selectNode(node: RowNode | IRowNode): void {
        if (node) {
            node.setSelected(true);
            // this.gridApi?.selectNode(node);
            this.gridApi?.ensureNodeVisible(node);

        }
    }

    scrollElementIntoView(): void {
        setTimeout(() => {
            const elementList = this.elemRef.nativeElement.querySelectorAll('.ag-row-selected');
            const element = _.find(elementList, elem => elem.children?.length);
            element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 1000);
    }

    getRows(): Array<RowNode> {
        const itemList = [];
        if (this.gridApi && !this.gridApi?.isDestroyed()) {
            this.gridApi.forEachNode(node => {
                itemList.push(node);
            });
        }
        return itemList;
    }

    refreshColumns(): void {
        if (this.gridApi) {
            this.gridApi.sizeColumnsToFit();
        }
    }

    setColumnDefs(colDefs, retry = true): void {
        if (this.gridApi && !this.gridApi?.isDestroyed()) {
            if (colDefs?.length > 0) {
                colDefs.forEach(colDef => {
                    colDef.cellRenderer = colDef.cellRenderer ? colDef.cellRenderer : 'gridCellRenderer';
                })
            }
            this.gridApi.setGridOption('columnDefs', colDefs);
        } else {
            setTimeout(() => {
                if (retry) {
                    this.setColumnDefs(colDefs, false);
                }
            });
        }
    }

    onHeaderClick(sortBy: string, sortAscending: boolean): void {
        if (this.gridContext.sortEnabled) {
            this.gridContext.sortBy = sortBy;
            this.gridContext.sortAscending = this.gridContext.sortTypeEnabled ? sortAscending : true;
            if (this.pagerVisible === false) {
                this.loadDataFinished();
            } else if (this.preferObservable) {
                this.loadObservableData();
            } else {
                this.loadData();
            }
        }
    }

    onHeaderContextMenu(item: GenericColDef, event: MouseEvent): void {
        if (item && event && this.headerContextMenu) {
            this.onContextMenu(this.headerContextMenu, event, item);
        }
    }

    onCellKeyDown(e) {
        if (e.event.ctrlKey && e.event.keyCode === 65) {
            e.event.target.select();
        }
    }

    onCellClick(event: CellClickedEvent, action: CellActionType): void {
        if (event && event.node && action) {
            if (action === CellActionType.expandCollapse) {
                event.node.selectable = true;
                this.expandCollapseRow(event.node as RowNode);
            } else if (action === CellActionType.contextMenu && this.contextMenu && <MouseEvent>event.event && <HTMLElement>event.event.target) {
                event.node.selectable = true;
                const mouseEvent = <MouseEvent>event.event;
                let clientRect: DOMRect;
                clientRect = (<HTMLElement>mouseEvent.target).getBoundingClientRect();
                if (clientRect.width === 0) {
                    clientRect = <DOMRect>{
                        left: mouseEvent.clientX, right: mouseEvent.clientX,
                        top: mouseEvent.clientY, bottom: mouseEvent.clientY,
                        height: 0, width: 0
                    };
                }
                const targetElement = <HTMLElement>{ getBoundingClientRect: () => clientRect };
                if (event.node) {
                    if (!this.contextMenuNodes) {
                        this.contextMenuNodes = new Array<RowNode>();
                    }
                    this.contextMenuNodes.push(event.node as RowNode);
                    if (!this.isGridWithDurationColumn) {
                        this.gridApi?.redrawRows({ rowNodes: [event.node] });
                    }
                }
                this.onContextMenu(this.contextMenu, mouseEvent, this.gridContext &&
                    this.gridContext.contextMenuItemMap ?
                    this.gridContext.contextMenuItemMap(event.node.data, this.commonService.mainContent.onContextMenuActionCallback) : event.node.data, targetElement);
            } else if (this.cellClickedCallback) {
                this.cellClickedCallback(event.node as RowNode, action, event);
            }
        }
    }

    onRowDoubleClicked(event: RowDoubleClickedEvent): void {
        if (event && event.node) {
            if (this.cellDoubleClickedCallback) {
                this.cellDoubleClickedCallback(event.node as RowNode);
            }
            if (this.rowSelection === GridRowSelectionType.single || this.rowSelection === GridRowSelectionType.multiple) {
                // reselect after double click
                event.node.setSelected(true);
            }
        }
    }

    checkSelectedItems(): void {
        if (this.gridCheckedItems.length > 0) {
            this.gridApi?.forEachNode((node) => {
                this.gridCheckedItems.forEach((entry) => {
                    if (_.isEqual(node.data, entry)) {
                        node.setSelected(true);
                    }
                });
            });
        }
    }

    getColumnFieldId(): string {
        if (this.gridApi) {
            const columnFieldId = _.filter(this.gridApi.getColumns(), (column) => column.colDef.filterType);
            if (columnFieldId && columnFieldId.length > 0) {
                return columnFieldId[0].colId;
            }
        }
    }

    onFilterApply(params: any, filterType: FilterTemplateType, filterColumn: string, isResetAllFilter?: boolean): void {
        if (params || filterType === FilterTemplateType.clear) {
            let viewFilters;
            if (this.requestInfo) {
                if (this.requestInfo.data?.startIndex) {
                    this.requestInfo.data.startIndex = 0;
                }
                if (!this.requestInfo.data?.filters) {
                    this.requestInfo.data.filters = [];
                }

                viewFilters = lo_.cloneDeep(_.filter(this.requestInfo.data.filters, (f) => f.attribute === 'viewType' ||
                    f.attribute === FilterAttribute.ERROREXIST || f.attribute === FilterAttribute.SYNTAXERROR || f.attribute === FilterAttribute.OPERATIONSTATE));
                if (viewFilters && viewFilters.length > 0) {
                    viewFilters[0].filterColumn = filterColumn;
                    viewFilters.forEach(vf => {
                        this.filters.splice(this.filters.indexOf(vf), 1);
                    });
                }
            }

            if (filterType === FilterTemplateType.clear && filterColumn === undefined) {
                if (this.requestInfo) {
                    this.requestInfo.data.filters = [];
                }
                this.filters = [];
                if (viewFilters && viewFilters.length > 0) {

                    viewFilters.forEach(vf => {
                        delete vf.filterColumn;
                        this.filters.push(vf);
                        this.requestInfo.data.filters.push(vf);
                    });
                }
            } else {
                this.removeCheckedOrFilteredItems('filters', filterColumn, 'filterColumn', isResetAllFilter);
            }
            this.setCheckedItems = true;
            this.removeCheckedOrFilteredItems('filters', filterColumn, 'filterColumn', isResetAllFilter);

            const filterValue = params.toLowerCase();
            let newFilter;
            const curentColumn = _.filter(this.gridApi?.getColumns(), (column) => column.colDef.headerName === filterColumn);
            switch (filterType) {
                case FilterTemplateType.freeform:
                    const attributeName = this.gridContext.isFilterNameChangeable ? filterColumn.replace(/\s+/g, '').toUpperCase() : 'name';
                    newFilter = {
                        attribute: curentColumn?.length > 0 && curentColumn[0].colDef.filterAttributeName ? curentColumn[0].colDef.filterAttributeName.toUpperCase() : attributeName,
                        value: filterValue,
                        operator: 'LIKE',
                        filterColumn: filterColumn,
                        index: this.setFilterIndex(filterColumn)
                    };
                    break;

                case FilterTemplateType.range:
                    newFilter = { attribute: 'name', value: filterValue, operator: 'EQUALS', filterColumn: filterColumn };
                    break;

                case FilterTemplateType.date:
                    const dates = filterValue.split('~~');
                    if (dates?.length == 2) {
                        const attribute = curentColumn?.length > 0 && curentColumn[0].colDef.filterAttributeName ? curentColumn[0].colDef.filterAttributeName.toUpperCase() : filterColumn.replace(/\s+/g, '').toUpperCase();
                        const startFilter = { attribute: attribute, value: dates[0].trim(), operator: 'AFTER', filterColumn: filterColumn };
                        this.filters.push(startFilter);
                        newFilter = { attribute: attribute, value: dates[1].trim(), operator: 'BEFORE', filterColumn: filterColumn };
                    }
                    break;

                case FilterTemplateType.multiChoice:
                    newFilter = { attribute: 'name', value: filterValue, operator: 'EQUALS', filterColumn: filterColumn };
                    break;

                case FilterTemplateType.singleChoice:
                    if (filterValue.toString() !== 'all') {
                        let valueToSend = filterValue === PasswordSourceType.FOUND_PASSWORDS.toLowerCase() ? PasswordSourceType.IMPORT.toUpperCase() : filterValue.toUpperCase();
                        if (curentColumn?.length > 0 && curentColumn[0].colDef.field === 'projectServiceName') {
                            const selectedValueParts = params.split(' (');
                            valueToSend = selectedValueParts[0];
                        }
                        newFilter = {
                            attribute: curentColumn?.length > 0 && curentColumn[0].colDef.filterAttributeName ? curentColumn[0].colDef.filterAttributeName.toUpperCase() : filterColumn.toUpperCase(),
                            value: valueToSend,
                            operator: 'EQUALS',
                            filterColumn: filterColumn
                        };
                    }
                    break;

                case FilterTemplateType.ownerSingleChoice:
                    this.filters = this.filters.filter(item => item.attribute !== 'OWNER');
                    if (filterValue.toString() === 'all') {
                        newFilter = { attribute: 'filteronowner', filterColumn: filterColumn };
                    } else {
                        newFilter = { attribute: 'OWNER', value: filterValue, operator: 'EQUALS', filterColumn: filterColumn };
                    }
                    break;

                case FilterTemplateType.comboBox:
                    newFilter = {
                        attribute: curentColumn?.length > 0 && curentColumn[0].colDef.filterAttributeName ? curentColumn[0].colDef.filterAttributeName.toUpperCase() : filterColumn.replace(/\s+/g, '').toUpperCase(),
                        value: filterValue,
                        operator: 'LIKE',
                        filterColumn: filterColumn
                    };
                    break;

                case FilterTemplateType.singleIconButtonSelection:
                case FilterTemplateType.multipleIconsButtonSelection:
                    newFilter = {
                        value: filterValue,
                        filterColumn: filterColumn,
                        hasMultipleFilteredValues: true,
                        filterType: filterType
                    }
                    break;
            }

            if (newFilter) {
                this.filters.push(newFilter);
            }

            if (viewFilters && viewFilters.length > 0) {
                viewFilters.forEach(vf => this.filters.push(vf));
            }
            const filtersToSend = lo_.cloneDeep(this.filters);
            filtersToSend.forEach((entry) => {
                delete entry.filterColumn;
                delete entry.index;
            });

            const serviceTierFilter = _.filter(this.gridApi?.getColumns(), (column) => column.colDef.headerName === filterColumn && !column.colDef.serviceTierFilter);
            if (serviceTierFilter && serviceTierFilter.length > 0) {
                this.isDataFiltered = true;
                this.allRowsSelected = false;
                if ((filterType === FilterTemplateType.clear || filterValue === 'all') && this.requestInfo) {
                    this.isDataFiltered = false;
                    if (this.gridContext.uiMultiFilter) {
                        this.gridContext.loadAndFilter = true;
                    }
                    if (this.preferObservable) {
                        this.loadObservableData();
                    } else {
                        this.loadData();
                    }
                } else if (filterType === FilterTemplateType.clear) {
                    this.isDataFiltered = false;
                    if (this.onCustomFilterCallback) {
                        this.onCustomFilterCallback();
                    }
                } else {
                    this.isBusy = true;
                    this.isBusyMessage = 'Loading ...';
                    const data = this.initialLoadData;
                    let dataFiltered = [];
                    this.filters = this.filters.sort((a, b) => {
                        return a.index - b.index;
                    });
                    if (this.gridContext.uiMultiFilter) {
                        if (this.gridContext.filterTreeStructure) {
                            dataFiltered = [];
                            data.forEach((row) => {
                                const filteredRow = this.filterTreeStructure(row);
                                if (filteredRow) {
                                    dataFiltered.push(filteredRow);
                                }
                            });
                        } else {
                            this.initialLoadData.forEach((row) => {
                                let matchesAllFilters = true;
                                this.filters.forEach((entry) => {
                                    const coldId = _.filter(this.gridApi?.getColumns(), (column) => column.colDef.headerName === entry.filterColumn);
                                    if (coldId?.length > 0) {
                                        const filterStrictlyOrNot = this.gridName === 'Members' && coldId[0].colId === 'type' && coldId[0].colDef.filterType === FilterTemplateType.singleChoice
                                            ? row[coldId[0].colId]?.toLowerCase() !== entry.value.toLowerCase()
                                            : row[coldId[0].colId]?.toLowerCase().indexOf(entry.value.toLowerCase()) === -1;
                                        if (!row[coldId[0].colId] || filterStrictlyOrNot) {
                                            matchesAllFilters = false;
                                        }
                                    } else {
                                        matchesAllFilters = false;
                                    }
                                });
                                if (matchesAllFilters) {
                                    dataFiltered.push(row);
                                }
                            });
                        }
                    } else {
                        data.forEach((entry) => {
                            if (filterType === FilterTemplateType.ownerSingleChoice && serviceTierFilter?.[0]?.colId === 'userStatus') {
                                if (entry[serviceTierFilter[0].colId].toString().toLowerCase() === filterValue) {
                                    dataFiltered.push(entry);
                                }
                            } else {
                                if (entry[serviceTierFilter[0]?.colId]?.toString().toLowerCase().indexOf(filterValue) > -1) {
                                    dataFiltered.push(entry);
                                }
                            }
                        });
                    }

                    this.loadAndFilter = false;
                    this.setData(dataFiltered);
                    this.isBusy = false;
                }
            } else {
                this.allRowsSelected = false;
                if (this.requestInfo) {
                    this.requestInfo.data.filters = filtersToSend;
                    if (this.preferObservable) {
                        this.loadObservableData();
                    } else {
                        this.loadData();
                    }
                }
            }
        }
    }

    filterTreeStructure(row) {
        let matchesAllFilters = true;
        this.filters.forEach((entry) => {
            const coldId = _.filter(this.gridApi?.getColumns(), (column) => column.colDef.headerName === entry.filterColumn);
            if (coldId?.length > 0) {
                const matchingKeywords = entry.hasMultipleFilteredValues ? entry.value.split(' ') : null;
                const matchingKeywordsFlag = matchingKeywords?.length > 0 && matchingKeywords.indexOf(row[coldId[0].colId]?.toLowerCase()) === -1;
                const rowColIDFlag = (!row[coldId[0].colId] || row[coldId[0].colId]?.toLowerCase().indexOf(entry.value.toLowerCase()) === -1) && !matchingKeywords;
                if (matchingKeywordsFlag || rowColIDFlag) {
                    matchesAllFilters = false;
                }
            }
        });
        if (matchesAllFilters) {
            return row;
        } else if (row?.children?.length > 0) {
            const parent = Object.assign({}, row);
            parent.children = [];
            row.children.forEach((child) => {
                const filteredChild = this.filterTreeStructure(child);
                if (filteredChild) {
                    parent.children.push(filteredChild);
                }
            });
            if (parent.children.length > 0) {
                return parent;
            } else {
                return null;
            }
        } else {
            return null;
        }
    }

    setFilterIndex(filterColumn): number {
        if (this.lastFilter?.filterColumn === filterColumn) {
            return this.lastFilter.index;
        } else {
            return this.filters.length - 1;
        }
    }

    removeCheckedOrFilteredItems(objectType, column, columnType, isResetAllFilter?: boolean): void {
        const index = _.indexOf(_.pluck(this[objectType], columnType), column);
        if (index > -1 && !isResetAllFilter) {
            this.lastFilter = this[objectType][index];
            this[objectType].splice(index, 1);
        }

        // this check is to send only one call the backend with empty filters in the payload when 'Reset All Filters' is selected.
        // Instead of making number of calls splicing out last element in the filter
        if (isResetAllFilter) {
            this.filters = [];
        }
    }

    onJobManagementTaskActionClick(action: CellActionType, item: any): void {
        if (this.cellClickedCallback) {
            this.cellClickedCallback(item, action);
        }
    }

    onCellContextMenu(params: ICellRendererParams, event: MouseEvent): void {
        if (params && event && this.contextMenu) {
            if (params.node) {
                if (this.rowSelection === GridRowSelectionType.multiple && !params.node.isSelected() && this.gridApi.getSelectedRows().length > 0) {
                    //this.gridApi.selectNode(params.node, false, false);
                    params.node.setSelected(true);
                } else {
                    if (!this.contextMenuNodes) {
                        this.contextMenuNodes = new Array<RowNode>();
                    }
                    this.contextMenuNodes.push(params.node as RowNode);
                    if (!this.isGridWithDurationColumn) {
                        this.gridApi?.redrawRows({ rowNodes: [params.node] });
                    }

                }
            }
            this.onContextMenu(this.contextMenu, event, this.gridContext && this.gridContext.contextMenuItemMap ?
                this.gridContext.contextMenuItemMap(params.data, this.commonService.mainContent.onContextMenuActionCallback) : params.data);
        }
    }

    disableContextMenuOptionsByParsingVersion(item: any): void {
        item.enabled = false;
        item.tooltipFunction = () => 'You can no longer perform this operation for a Project using Parsing Library Version V1.';
        item.tooltipAlwaysOn = true;
    }

    onContextMenu(contextMenu: ContextMenuComponent, event: MouseEvent, item: any, anchorElement?: any): void {
        const isJobNodeWithDisabledContextMenu = item && (item instanceof Job) && (item as Job).hasDisabledContextMenu;
        if (contextMenu && event && this.hasContextMenuPermissions && !isJobNodeWithDisabledContextMenu) {
            if (item) {
                event.preventDefault();
                event.stopPropagation();
                this.commonWithRestService.enableDisableWordListOptions(item);
                item.isHeaderContextMenu = item.headerName ? true : false;
                item.contextMenuOptions = item.contextMenuOptions ? item.contextMenuOptions : item.headerName ? this.headerContextMenuOptions : this.contextMenuOptions;
                const projectParsingVersion = this.commonService?.currentProject?.attributes?.find(a => a?.name === 'parsingVersion')?.value;
                const itemReindexCM = item?.contextMenuOptions?.find(o => o?.name === 'Reindex ...');
                const itemLoadFromExternalCM = item?.contextMenuOptions?.find(o => o?.name === 'Load from External Area ...');
                const itemCreateDsFromSearchResultCM = item?.contextMenuOptions?.find(o => o?.name === 'Create Data Set from Search Results ...');
                if (itemReindexCM && projectParsingVersion === 'V1') {
                    this.disableContextMenuOptionsByParsingVersion(itemReindexCM);
                } else if (itemReindexCM) {
                    itemReindexCM.enabled = !(item?.data as Corpus).busy &&
                        ((item?.data as Corpus)?.representationType === RepresentationSet.NO_INDEX || (item?.data as Corpus)?.representationType === RepresentationSet.CONTENT_INDEX) &&
                        ((item?.data as Corpus)?.sharedState !== SharedState.SHARED || ((item?.data as Corpus)?.sharedState === SharedState.SHARED && (item?.data as Corpus)?.handle?.startsWith((item?.data as Corpus)?.owner)));
                    itemReindexCM.tooltipAlwaysOn = false;
                    itemReindexCM.tooltipFunction = (item: GenericTreeNode) => (item?.data as Corpus)?.sharedState === SharedState.SHARED && !((item?.data as Corpus)?.handle?.startsWith((item?.data as Corpus)?.owner)) ? 'Action not allowed for a Data Set shared to this project.' : '';
                }
                if (itemLoadFromExternalCM && ((item?.data as SearchResult)?.viewType === ViewType.CORPUS_SET_RESULT || (item?.data as SearchResult)?.viewType === ViewType.CORPUS_RESULT) && projectParsingVersion === 'V1') {
                    this.disableContextMenuOptionsByParsingVersion(itemLoadFromExternalCM);
                } else if (itemLoadFromExternalCM) {
                    itemLoadFromExternalCM.enabled = (item?.data as SearchResult).documentCount > 0;
                    itemLoadFromExternalCM.tooltipAlwaysOn = false;
                    itemLoadFromExternalCM.tooltipFunction = (item: GenericTreeNode) => '';
                }
                if (itemCreateDsFromSearchResultCM && ((item?.data as SearchResult)?.viewType === ViewType.CORPUS_SET_RESULT || (item?.data as SearchResult)?.viewType === ViewType.CORPUS_RESULT) && projectParsingVersion === 'V1') {
                    this.disableContextMenuOptionsByParsingVersion(itemLoadFromExternalCM);
                } else if (itemCreateDsFromSearchResultCM) {
                    itemCreateDsFromSearchResultCM.enabled = (item?.data as SearchResult)?.documentCount > 0;
                    itemCreateDsFromSearchResultCM.tooltipAlwaysOn = false;
                    itemCreateDsFromSearchResultCM.tooltipFunction = (item: GenericTreeNode) => '';
                }
                this.contextMenu.open(event, item);
                if (this.commonService.matrixGridsSelection) {
                    this.commonService.matrixGridsSelection.openFromDocumentActionBtn = anchorElement?.type === 'button' && anchorElement?.textContent === 'Document' && !isNullOrUndefined(this.commonService.matrixGridsSelection?.viewHandle);
                }
            }
        }
    }

    onContextMenuClose() {
        if (this.contextMenuNodes && this.contextMenuNodes.length > 0 && !this.isGridWithDurationColumn) {
            this.gridApi?.redrawRows({ rowNodes: this.contextMenuNodes.splice(0, 1) });
        }
    }

    expandCollapseRow(item: RowNode, switchExpander = true, refreshColumns = true): void {
        if (item && item.data && item.data.isParent && item.data.children) {
            if (switchExpander) {
                item.data.isExpanded = !item.data.isExpanded;
            }
            if (item.data.isExpanded) {
                item.data.children.forEach((element, index) => {
                    this.gridApi?.applyTransaction({
                        add: [element],
                        addIndex: item.rowIndex + index + 1
                    });
                });
                if (this.rowsHaveTreeGrouping) {
                    item.data.children.forEach(element => {
                        if (element.isParent && element.children) {
                            const childNode = this.findNode((node: RowNode) => node.data === element, false);
                            if (childNode) {
                                element.isExpanded = this.gridName === 'Metadata' ? true : false;
                                this.expandCollapseRow(childNode, false, false);
                            }
                        }
                    });
                }
            } else {
                let nodesToBeRemoved = [];
                let flattenedClusterList = [];
                if (this.rowsHaveTreeGrouping) {
                    item.data.children.forEach(element => {
                        if (element.isParent && element.children) {
                            const childNode = this.findNode((node: RowNode) => node.data === element, false);
                            if (childNode) {
                                element.isExpanded = false;
                                this.expandCollapseRow(childNode, false, false);
                            }
                        }
                    });
                }

                if (item.data.hasMultiLevelGroups) {
                    // this allows a single selected row only - may have to enhance the below code if there is a multiple row selection
                    const selectedNodeClusterID = this.gridApi?.getSelectedRows()[0].clusterID;
                    flattenedClusterList = lo_.flatMapDeep(item.data.children, this.getAllClusters);
                    nodesToBeRemoved = this.nodesToBeRemoved(flattenedClusterList);
                    // check if there is a selected row in the nodesToBeRemoved, 
                    // if yes, make the row that is expanded the selected row 
                    // as there is no selection on the child row anymore
                    const hasSelectedChildNode = this.checkforChildRowSelection(nodesToBeRemoved, selectedNodeClusterID);
                    if (hasSelectedChildNode) {
                        // select the parent or the collapsed row
                        item.setSelected(true);
                    }
                    this.flattenedChildRows = [];
                } else {
                    this.gridApi?.applyTransaction({
                        remove: item.data.children
                    })
                }
            }
            this.gridApi?.redrawRows({ rowNodes: [item] });
            if (refreshColumns) {
                this.refreshColumns();
            }
        }
    }

    getAllClusters = (cluster) => {
        if (cluster?.children?.length > 0) {
            return [cluster, lo_.flatMapDeep(cluster.children, this.getAllClusters)];
        } else {
            return cluster;
        }
    }

    nodesToBeRemoved = (flattenedClusterList: Array<any>) => {
        const nodesToBeRemoved = [];
        flattenedClusterList.forEach((child) => {
            this.gridApi?.getRenderedNodes()?.forEach((renderedNode) => {
                if (child.handle === renderedNode.data.handle) {
                    nodesToBeRemoved.push(renderedNode.data);
                    this.removeNodes(nodesToBeRemoved);
                    if (renderedNode.data?.children?.length > 0) {
                        const allSubClusters = lo_.flatMapDeep(renderedNode.data.children, this.getAllClusters);
                        this.nodesToBeRemoved(allSubClusters);
                        this.removeNodes(nodesToBeRemoved);
                    }
                }
            });

        });
        return nodesToBeRemoved;
    }

    // method to check if there are any selected rows when a parent row is collapsed
    checkforChildRowSelection = (nodesToBeRemoved: Array<any>, selectedNodeClusterID: string) => {
        nodesToBeRemoved.forEach(cluster => {
            const allChildClusters = this.getAllClusters(cluster);
            if (allChildClusters instanceof Array) {
                const flattenedChildNodes = _.flatten(allChildClusters);
                flattenedChildNodes.forEach(flattenedCluster => {
                    this.flattenedChildRows.push(flattenedCluster);
                });
            } else if (allChildClusters instanceof Object) {
                this.flattenedChildRows.push(allChildClusters);
            }
        });
        return (this.flattenedChildRows.findIndex(childRow => childRow.clusterID === selectedNodeClusterID)) !== -1 ? true : false;
    }

    removeNodes = (nodes) => {
        // remove the node only if it is existing in the grid
        const filteredNodestoBeRemoved = [];
        nodes.forEach(node => {
            const rowNodeIndex = this.gridApi?.getRenderedNodes().findIndex(renderedNode => {
                return node.clusterID === renderedNode.data.clusterID;
            });
            if (rowNodeIndex !== -1) {
                filteredNodestoBeRemoved.push(node);
            }
        });
        this.gridApi?.applyTransaction({
            remove: filteredNodestoBeRemoved
        })
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);

    ngOnDestroy(): void {
        if (this.entityChangedEvent) {
            this.entityChangedEvent.unsubscribe();
        }
        if (this.subscription && !this.subscription.closed) {
            this.subscription.unsubscribe();
        }
    }
}
